import React from 'react';
import { useQuery } from '@apollo/client';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaInfoCircle, FaLeaf, FaMapMarkerAlt } from 'react-icons/fa';
import { gqlGetRecyclingImpactStats } from './graphql';

interface Co2ImpactSectionProps {
  id: number;
}

/** Νομική/ενημερωτική αποποίηση για τις εκτιμήσεις CO₂ */
const CO2_DISCLAIMER =
  'Οι εκτιμήσεις εξοικονόμησης εκπομπών CO₂ που παρουσιάζονται προκύπτουν από το CO₂ Calculator Tool του WEEE Forum και βασίζονται σε γενικές παραδοχές και συντελεστές υπολογισμού. Τα αποτελέσματα παρέχονται αποκλειστικά για ενημερωτικούς και επικοινωνιακούς σκοπούς και αποσκοπούν στην ανάδειξη των περιβαλλοντικών οφελών της ανακύκλωσης ΑΗΗΕ. Οι τιμές αποτελούν εκτιμήσεις και δεν συνιστούν πιστοποιημένα, επαληθευμένα ή ελεγμένα περιβαλλοντικά δεδομένα. Ως εκ τούτου, δεν θα πρέπει να χρησιμοποιούνται ως αποδεικτικά στοιχεία ή για σκοπούς συμμόρφωσης, πιστοποίησης, κανονιστικής αναφοράς, ESG/CSRD reporting, υπολογισμού ανθρακικού αποτυπώματος ή άλλων επίσημων διαδικασιών χωρίς ανεξάρτητη επαλήθευση.';

/** Μετατροπή τόνων σε κιλά και μορφοποίηση με ελληνικό locale */
const formatKg = (tons: number) =>
  (tons * 1000).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Κάρτα στατιστικού βάρους / CO₂ */
const ImpactStat = ({
  label,
  wasteTons,
  co2Tons,
}: {
  label: string;
  wasteTons: number;
  co2Tons: number;
}) => (
  <div className="home-co2-stat">
    <span className="home-co2-stat__label">{label}</span>
    <div className="home-co2-stat__row">
      <span className="home-co2-stat__metric">Απόβλητα</span>
      <strong>{formatKg(wasteTons)} kg</strong>
    </div>
    <div className="home-co2-stat__row home-co2-stat__row--co2">
      <span className="home-co2-stat__metric">CO₂ εξοικονομήθηκε</span>
      <strong>{formatKg(co2Tons)} kg</strong>
    </div>
  </div>
);

export const Co2ImpactSection: React.FC<Co2ImpactSectionProps> = ({ id }) => {
  const year = new Date().getFullYear();

  const { data, loading } = useQuery(gqlGetRecyclingImpactStats, {
    variables: { collectionPointId: id, year },
    skip: !id,
  });

  const stats = data?.getRecyclingImpactStats;

  return (
    <Card className="home-section-card shadow-sm mt-4">
      <Card.Header className="d-flex align-items-center gap-2">
        <Card.Title className="m-0 d-flex align-items-center home-section-title">
          <FaLeaf className="text-success mr-2" />
          <span className="mr-2">Εξοικονόμηση CO₂ από ανακύκλωση ΑΗΗΕ</span>
          <OverlayTrigger
            trigger="click"
            rootClose
            placement="bottom"
            overlay={
              <Popover id="co2-impact-disclaimer">
                <Popover.Content className="home-co2-disclaimer text-left">
                  {CO2_DISCLAIMER}
                </Popover.Content>
              </Popover>
            }
          >
            <span
              className="home-co2-info"
              role="button"
              tabIndex={0}
              aria-label="Πληροφορίες για τις εκτιμήσεις CO₂"
            >
              <FaInfoCircle />
            </span>
          </OverlayTrigger>
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {loading || !stats ? (
          <p className="text-muted text-center py-2 mb-0">Φόρτωση στατιστικών...</p>
        ) : (
          <div className="home-co2-grid">
            <div className="home-co2-block">
              <div className="home-co2-block__title">
                <FaMapMarkerAlt />
                <span>Το σημείο σας ({year})</span>
              </div>
              <ImpactStat
                label="Σημείο συλλογής"
                wasteTons={stats.collectionPointWasteWeightTons}
                co2Tons={stats.collectionPointCo2SavedTons}
              />
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
